


import React, { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

interface ReauthDeleteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    user: firebase.User | null;
    itemCount: number;
}

const ReauthDeleteModal: React.FC<ReauthDeleteModalProps> = ({ isOpen, onClose, onConfirm, user, itemCount }) => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setPassword('');
            setError(null);
            setLoading(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !user.email) {
            setError('No se ha podido verificar el usuario.');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const credential = firebase.auth.EmailAuthProvider.credential(user.email, password);
            await (user as any).reauthenticateWithCredential(credential);
            await onConfirm();
        } catch (err: any) {
             if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError('La contraseña es incorrecta.');
            } else {
                setError('Ha ocurrido un error al verificar tu identidad.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div 
                className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-md border border-light-border dark:border-border"
                onClick={e => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold text-red-500 mb-4">Confirmar Eliminación</h2>
                <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                    Estás a punto de eliminar permanentemente <strong>{itemCount}</strong> transacciones. Esta acción no se puede deshacer.
                </p>
                 <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                    Por seguridad, por favor, introduce tu contraseña para confirmar.
                </p>
                <form onSubmit={handleSubmit}>
                    <label htmlFor="password-reauth" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Contraseña</label>
                    <input
                        type="password"
                        id="password-reauth"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        autoFocus
                        className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
                    <div className="flex justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                        <button type="submit" disabled={loading || !password} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:bg-gray-500">
                            {loading ? 'Eliminando...' : 'Confirmar y Eliminar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReauthDeleteModal;